// Vendor-agnostic event tracking.
//
// Feature code calls `track('book_now_click', { salon_id })` and never touches
// gtag / dataLayer / any vendor SDK directly (design rule #9). This module is
// the single seam. Each event goes two ways (BUG-479):
//   1. pushed onto `window.dataLayer` as `{ event, ...params }` for GTM, as before;
//   2. sent to GA4 with `gtag('event', name, params)` when gtag.js is loaded.
// Step 2 exists because GTM forwards none of these events to GA4 and gtag.js
// ignores plain-object dataLayer pushes: before it, GA4 only ever saw its
// automatic events. Ecommerce events (ecommerce.ts) carry an `items` array;
// for GTM they are pushed in GTM's ecommerce shape `{ event, ecommerce }`.
//
// Every parameter goes through redactParams (redact.ts) first: no UUID, no
// order id or payment session, no name, e-mail, phone or address.
//
// Consent: events are only forwarded after the visitor has granted cookie
// consent. Before that, `track()` is a no-op to the network - nothing is
// pushed, no identifier leaves the browser. The moment consent flips to
// `granted` the analytics loaders (gtm.ts / ga.ts) inject GTM, and subsequent
// track() calls flow through. We intentionally do NOT buffer pre-consent
// events: a denied visitor must leave no analytics trace.
//
// IMPORTANT for callers: the user-facing action (open booking, place call,
// navigate) must never depend on track() succeeding. track() is a fire-and-
// forget side effect - call it, then do the action regardless.

import { consent } from '../consent'
import { isGa4Loaded } from './ga'
import { redactParams, redactUrl } from './redact'

/** The canonical event names. One enum so GA4 config and code can't drift. */
export type AnalyticsEvent =
  // Tier 1 - conversions
  | 'book_now_click'
  | 'salon_selected'
  | 'booking_handoff'
  | 'call_salon_click'
  | 'checkout_handoff'
  // Tier 2 - high intent
  | 'salon_picker_open'
  | 'call_salon_open'
  | 'concierge_open'
  | 'concierge_message'
  | 'concierge_feedback'
  | 'concierge_menu_select'
  | 'concierge_menu_link'
  // Tier 3 - content engagement
  | 'service_view'
  | 'trichology_view'
  | 'reel_play'
  | 'reel_complete'
  | 'product_view'
  | 'guide_view'
  // Tier 4 - secondary
  | 'newsletter_signup'
  | 'contact_form_submit'
  | 'photo_consent_submit'
  | 'cart_add_item'
  | 'cart_remove_item'
  | 'faq_open'
  // Tier 5 - consent telemetry
  | 'consent_granted'
  | 'consent_denied'
  // GA4 recommended ecommerce events (ecommerce.ts builds their payloads)
  | EcommerceEvent

/** GA4's recommended ecommerce event names, sent with an `items` array. */
export type EcommerceEvent =
  | 'view_item_list'
  | 'select_item'
  | 'view_item'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'view_cart'
  | 'begin_checkout'
  | 'add_shipping_info'
  | 'add_payment_info'
  | 'purchase'

const ECOMMERCE_EVENTS: ReadonlySet<string> = new Set<EcommerceEvent>([
  'view_item_list',
  'select_item',
  'view_item',
  'add_to_cart',
  'remove_from_cart',
  'view_cart',
  'begin_checkout',
  'add_shipping_info',
  'add_payment_info',
  'purchase',
])

/** One GA4 ecommerce item. Never personal data. */
export interface GaItem {
  /** SKU (never a product UUID). */
  item_id: string
  item_name: string
  item_brand?: string
  item_category?: string
  item_variant?: string
  item_list_name?: string
  index?: number
  /** Unit price in DKK. */
  price?: number
  quantity?: number
}

/** Event parameters: flat primitives (GA4 custom dimensions), plus `items` on ecommerce events. */
export type EventParams = Record<string, string | number | boolean | undefined | GaItem[]>

export interface TrackOptions {
  /**
   * false: push to GTM's dataLayer only, never to GA4. For legacy alias
   * events (cart_add_item, cart_remove_item, product_view) that GA4 already
   * receives under their recommended ecommerce name, so GA4 never counts twice.
   */
  ga4?: boolean
}

interface DataLayerWindow extends Window {
  dataLayer?: Array<unknown>
  gtag?: (...args: unknown[]) => void
}

/**
 * Send an event to GTM's dataLayer and to GA4, gated on consent.
 *
 * @param event   one of the canonical AnalyticsEvent names
 * @param params  optional parameters (salon_id, source, value, items, ...)
 * @param options `{ ga4: false }` keeps the event out of GA4 (legacy aliases)
 */
export function track(event: AnalyticsEvent, params: EventParams = {}, options: TrackOptions = {}): void {
  // Consent gate. Tier-5 consent events are the sole exception: they describe
  // the consent decision itself and must record the moment consent is granted
  // (denied is handled separately - see trackConsent below).
  if (consent.get() !== 'granted' && event !== 'consent_granted') return

  const w = window as DataLayerWindow
  w.dataLayer = w.dataLayer ?? []

  // Undefined params and anything sensitive are dropped, UUIDs replaced.
  const clean = redactParams(params)

  if (ECOMMERCE_EVENTS.has(event)) {
    // GTM's ecommerce convention: clear the previous ecommerce object first,
    // or GTM merges the old items into this event.
    w.dataLayer.push({ ecommerce: null })
    w.dataLayer.push({ event, ecommerce: clean })
  } else {
    w.dataLayer.push({ event, ...clean })
  }

  // GA4 directly. gtag only exists once ga.ts loaded it (consent granted, on
  // a production build); otherwise there is nothing to send to. Each event
  // names its own (redacted) page: gtag otherwise keeps the page it loaded on
  // for every event after a client-side navigation.
  if (options.ga4 !== false && typeof w.gtag === 'function' && isGa4Loaded()) {
    w.gtag('event', event, { ...clean, page_location: redactUrl(window.location.href) })
  }
}

/**
 * Record the consent decision itself.
 *
 * `granted` rides through `track()` (which now passes its gate). `denied`
 * cannot - by definition there's no consented analytics to receive it - so we
 * surface it as a return signal callers may forward to a cookieless counter if
 * one is ever added. Today it is a deliberate no-op-to-GA, keeping a declined
 * visitor analytics-free while still centralising the decision in one place.
 */
export function trackConsent(decision: 'granted' | 'denied'): void {
  if (decision === 'granted') {
    track('consent_granted')
  }
  // decision === 'denied': intentionally not forwarded to GA4.
}
